import React, { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { CircularProgress } from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";
import { MdDownload, MdMap, MdPeople, MdRefresh, MdTableChart } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { saveAs } from "file-saver";
import Request from "../../Request";
import { logout } from "../../actions/AppActions";
import GeneralCandidatesReport from "../Faculdades/Dashboard/Cards/GeneralCandidatesReport";
import GeoIpDashboard from "../Faculdades/Dashboard/Cards/GeoIpDashboard";
import Search from "../ReusableComponents/Search";
import "../Faculdades/Dashboard/Cards/DashboardReports.css";

const jobStatusText = status => {
    if (status === "0") return "Pendente";
    if (status === "1") return "Executando";
    if (status === "2") return "Concluído";
    if (status === "3") return "Erro";
    return "-";
};

const defaultPagination = { current_page: 1, last_page: 1, total: 0 };

const reportBadgeClass = value => {
    const normalized = String(value || "").toLowerCase();
    if (["sim", "concluído", "concluido", "aprovado"].includes(normalized)) return "success";
    if (["não", "nao", "pendente", "executando"].includes(normalized)) return "warning";
    if (["erro", "reprovado"].includes(normalized)) return "danger";
    return "neutral";
};

const RelatóriosGerais = props => {
    const { faculdade_id, faculdade_name } = props;
    const dispatch = useDispatch();
    const user = useSelector(state => state.AppReducer.user);
    const [loadingCandidatos, setLoadingCandidatos] = useState(false);
    const [loadingGabaritos, setLoadingGabaritos] = useState(false);
    const [loadingResultados, setLoadingResultados] = useState(false);
    const [activeReport, setActiveReport] = useState(null);
    const [reportRows, setReportRows] = useState([]);
    const [reportHeadings, setReportHeadings] = useState([]);
    const [reportPagination, setReportPagination] = useState(defaultPagination);
    const [reportSearch, setReportSearch] = useState("");
    const [reportJob, setReportJob] = useState(null);
    const [reportMessage, setReportMessage] = useState("");

    const handleAuth = response => {
        if (response?.message === "Unauthenticated.") {
            dispatch(logout());
            return true;
        }
        return false;
    };

    const setErrorMessage = response => {
        if (response?.errors) {
            setReportMessage(response.errors.error || "Não foi possível carregar o relatório.");
            return true;
        }
        return false;
    };

    const getLoading = () => {
        if (activeReport?.key === "answers") return loadingGabaritos;
        if (activeReport?.key === "results") return loadingResultados;
        if (activeReport?.key === "login" || activeReport?.key === "permissions") return loadingCandidatos;
        return false;
    };

    const loadSimpleReport = async (type, page = 1, searchValue = reportSearch, shouldDownload = false) => {
        setLoadingCandidatos(true);
        setReportMessage(shouldDownload ? "Gerando planilha..." : "Carregando relatório...");

        const params = new URLSearchParams();
        params.append("page", shouldDownload ? 1 : page);
        params.append("per_page", shouldDownload ? 10000 : 20);
        if (searchValue) params.append("search", searchValue);

        const response = await Request.Get(`faculdade/get_relatorio_geral/${faculdade_id}/${type}?${params.toString()}`);
        setLoadingCandidatos(false);
        if (response === undefined || handleAuth(response) || setErrorMessage(response)) return;

        const rows = response.result || [];
        if (shouldDownload) {
            let csv = "Nome;CPF;Número de Inscrição\n";
            rows.forEach(item => {
                csv += `${item.name};${item.cpf || ""};${item.numero_inscrição || ""}\n`;
            });

            const blob = new Blob([csv], { type: "text/plain" });
            saveAs(blob, `relatorio_${type}_${moment().format("YYYY-MM-DD-HH:mm:ss")}.csv`);
            setReportMessage("Relatório gerado com sucesso.");
            return;
        }

        setReportHeadings(["Nome", "CPF", "Número de inscrição"]);
        setReportRows(rows.map(item => [item.name, item.cpf || "-", item.numero_inscrição || "-"]));
        setReportPagination(response.pagination || { ...defaultPagination, total: rows.length });
        setReportMessage("Prévia carregada. Use o botão de baixar para gerar a planilha.");
    };

    const loadGabaritos = async (page = 1, searchValue = reportSearch) => {
        setLoadingGabaritos(true);
        setReportMessage("Carregando relatório...");

        const params = new URLSearchParams();
        params.append("page", page);
        params.append("per_page", 20);
        if (searchValue) params.append("search", searchValue);

        const response = await Request.Get(`faculdade/get_gabaritos_faculdade/${faculdade_id}?${params.toString()}`);
        setLoadingGabaritos(false);
        if (response === undefined || handleAuth(response) || setErrorMessage(response)) return;

        setReportHeadings(response.headings || []);
        setReportRows((response.rows || []).map(item => [item.processo_name || "-", item.prova_name || "-", ...(item.gabarito || [])]));
        setReportPagination(response.pagination || defaultPagination);
        setReportJob(response.job || null);
        setReportMessage("Prévia carregada. Use o botão de baixar para gerar a planilha.");
    };

    const loadResultados = async (page = 1, searchValue = reportSearch) => {
        setLoadingResultados(true);
        setReportMessage("Carregando relatório...");

        const params = new URLSearchParams();
        params.append("page", page);
        params.append("per_page", 20);
        if (searchValue) params.append("search", searchValue);

        const response = await Request.Get(`faculdade/get_resultados_faculdade/${faculdade_id}?${params.toString()}`);
        setLoadingResultados(false);
        if (response === undefined || handleAuth(response) || setErrorMessage(response)) return;

        setReportHeadings(response.headings || []);
        setReportRows(response.rows || []);
        setReportPagination(response.pagination || defaultPagination);
        setReportJob(response.job || null);
        setReportMessage("Prévia carregada. Use o botão de baixar para gerar a planilha.");
    };

    const downloadGabaritos = async () => {
        setLoadingGabaritos(true);
        setReportMessage("Solicitando geração da planilha...");
        const response = await Request.Get(`faculdade/download_gabaritos_faculdade/${faculdade_id}`);
        setLoadingGabaritos(false);
        if (response === undefined || handleAuth(response) || setErrorMessage(response)) return;

        if (response.path) {
            window.open(response.path, "_blank");
            setReportMessage("Planilha gerada. O download foi aberto em uma nova aba.");
        } else if (response.message) {
            setReportMessage(response.message);
        }
        loadGabaritos(reportPagination.current_page || 1, reportSearch);
    };

    const downloadResultados = async () => {
        setLoadingResultados(true);
        setReportMessage("Solicitando geração da planilha...");
        const response = await Request.Get(`faculdade/download_resultados_faculdade/${faculdade_id}`);
        setLoadingResultados(false);
        if (response === undefined || handleAuth(response) || setErrorMessage(response)) return;

        if (response.path) {
            window.open(response.path, "_blank");
            setReportMessage("Planilha gerada. O download foi aberto em uma nova aba.");
        } else if (response.message) {
            setReportMessage(response.message);
        }
        loadResultados(reportPagination.current_page || 1, reportSearch);
    };

    const title = user?.empresa == null || user?.empresa?.change_text === false ? "da instituição" : "do processo";
    const reports = [
        { key: "general_candidates", title: "Relatório Geral de candidatos", description: "Saídas de tela, desconexões, eventos de IA, IP e geolocalização.", icon: <MdPeople /> },
        { key: "geo_ip", title: "Mapa e geolocalização por IP", description: "Mapa de calor e planilha com localização dos candidatos por IP.", icon: <MdMap /> },
        { key: "login", title: "Quem já fez login", description: "Candidatos que registraram acesso no sistema.", icon: <MdTableChart />, action: () => loadSimpleReport("login"), download: () => loadSimpleReport("login", 1, reportSearch, true) },
        { key: "permissions", title: "Quem concedeu permissões", description: "Candidatos que chegaram na tela de avaliação não iniciada.", icon: <MdTableChart />, action: () => loadSimpleReport("tela_avaliacao_nao_iniciada"), download: () => loadSimpleReport("tela_avaliacao_nao_iniciada", 1, reportSearch, true) },
        { key: "answers", title: "Gabaritos das provas objetivas", description: "Planilha com os gabaritos das provas objetivas.", icon: <MdDownload />, action: () => loadGabaritos(), download: downloadGabaritos },
        { key: "results", title: "Resultados gerais das provas objetivas", description: "Planilha geral de resultados das provas objetivas.", icon: <MdDownload />, action: () => loadResultados(), download: downloadResultados },
    ];

    const openReport = report => {
        setActiveReport(report);
        setReportRows([]);
        setReportHeadings([]);
        setReportPagination(defaultPagination);
        setReportSearch("");
        setReportJob(null);
        setReportMessage("");
        if (report.action) report.action();
    };

    const refreshActiveReport = (page = reportPagination.current_page || 1, searchValue = reportSearch) => {
        if (activeReport?.key === "login") return loadSimpleReport("login", page, searchValue);
        if (activeReport?.key === "permissions") return loadSimpleReport("tela_avaliacao_nao_iniciada", page, searchValue);
        if (activeReport?.key === "answers") return loadGabaritos(page, searchValue);
        if (activeReport?.key === "results") return loadResultados(page, searchValue);
        return null;
    };

    const downloadActiveReport = () => {
        if (activeReport?.key === "login") return loadSimpleReport("login", 1, reportSearch, true);
        if (activeReport?.key === "permissions") return loadSimpleReport("tela_avaliacao_nao_iniciada", 1, reportSearch, true);
        if (activeReport?.key === "answers") return downloadGabaritos();
        if (activeReport?.key === "results") return downloadResultados();
        return null;
    };

    useEffect(() => {
        if (activeReport == null || activeReport.key === "general_candidates" || activeReport.key === "geo_ip") return;
        const timeout = setTimeout(() => refreshActiveReport(1, reportSearch), 450);
        return () => clearTimeout(timeout);
    }, [reportSearch]);

    useEffect(() => {
        if (reportJob == null || (reportJob.status !== "0" && reportJob.status !== "1")) return;
        const interval = setInterval(() => refreshActiveReport(reportPagination.current_page || 1, reportSearch), 30000);
        return () => clearInterval(interval);
    }, [reportJob, activeReport, reportPagination.current_page, reportSearch]);

    const renderPreviewTable = () => (
        <>
            <div className="dashboard-report-filter">
                <Search
                    hide_class
                    className="dashboard-report-search"
                    placeholder={activeReport?.key === "answers" ? "Processo ou prova" : "Nome, CPF ou inscrição"}
                    ValueChange={value => setReportSearch(value)}
                />
            </div>

            <div className="table-responsive dashboard-report-table">
                <table className="table table-striped">
                    <thead>
                        <tr>
                            {reportHeadings.map((heading, index) => <th key={`${heading}-${index}`}>{heading}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {reportRows.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                {row.map((value, columnIndex) => {
                                    const heading = reportHeadings[columnIndex] || "";
                                    const shouldBadge = columnIndex < 2 || /^Q\d+/i.test(heading) || ["CPF", "Número de inscrição", "Inscrição"].includes(heading);
                                    return (
                                        <td key={`${rowIndex}-${columnIndex}`}>
                                            {shouldBadge
                                                ? <span className={`dashboard-report-badge ${reportBadgeClass(value)}`}>{value || "-"}</span>
                                                : value || "-"}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
                {getLoading() && <div className="dashboard-report-loading"><CircularProgress /></div>}
                {!getLoading() && reportRows.length === 0 && <div className="dashboard-report-empty">Nenhum registro encontrado.</div>}
            </div>

            <Pagination
                className={reportPagination.last_page === 1 ? "hide" : ""}
                style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}
                count={reportPagination.last_page || 1}
                value={reportPagination.current_page || 1}
                onChange={(_, value) => refreshActiveReport(value, reportSearch)}
            />
        </>
    );

    const renderModalContent = () => {
        if (activeReport == null) return null;
        if (activeReport.key === "general_candidates") return <GeneralCandidatesReport faculdade_id={faculdade_id} embedded />;
        if (activeReport.key === "geo_ip") return <GeoIpDashboard faculdade_id={faculdade_id} embedded />;

        return (
            <div className="dashboard-report-modal-section">
                <div className="dashboard-report-header compact">
                    <div>
                        <h5>{activeReport.title}</h5>
                        <p>{activeReport.description}</p>
                        {reportJob != null && <span className="dashboard-report-job">
                            Status do Excel: <b>{jobStatusText(reportJob.status)}</b>
                            {reportJob.total_progresso > 0 && <> ({reportJob.progresso || 0} de {reportJob.total_progresso})</>}
                        </span>}
                        {reportMessage && <span className="dashboard-report-job">{reportMessage}</span>}
                    </div>
                    <div className="dashboard-report-actions">
                        <Button variant="light" onClick={() => refreshActiveReport()} disabled={getLoading()}>
                            {getLoading() ? <CircularProgress size={16} style={{ marginRight: 8 }} /> : <MdRefresh style={{ marginRight: 8 }} />}
                            Atualizar
                        </Button>
                        {activeReport.download && <Button variant="success" onClick={downloadActiveReport} disabled={getLoading()}>
                            {getLoading() ? <CircularProgress size={16} style={{ color: "white", marginRight: 8 }} /> : <MdDownload style={{ marginRight: 8 }} />}
                            Baixar planilha
                        </Button>}
                    </div>
                </div>

                {renderPreviewTable()}
            </div>
        );
    };

    return (
        <div className="dashboard-report-card dashboard-report-list-card">
            <div className="dashboard-report-header">
                <div>
                    <h5>Relatórios gerais</h5>
                    <p>Relatórios consolidados {title} "{faculdade_name}". Abra um relatório para visualizar, atualizar e baixar a planilha.</p>
                </div>
            </div>

            <div className="table-responsive dashboard-report-table dashboard-report-options">
                <table className="table table-striped">
                    <thead>
                        <tr>
                            <th>Relatório</th>
                            <th>Descrição</th>
                            <th data-priority="3">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reports.map(report => (
                            <tr key={report.key}>
                                <td><span className="dashboard-report-option-title">{report.icon}{report.title}</span></td>
                                <td>{report.description}</td>
                                <td>
                                    <Button
                                        variant="success"
                                        size="sm"
                                        className="inline-flex align-center jcc"
                                        onClick={e => {
                                            e.stopPropagation();
                                            openReport(report);
                                        }}
                                    >
                                        <MdDownload size={17} color="white" />
                                        <span className="ml-1">Abrir</span>
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Modal show={activeReport != null} onHide={() => setActiveReport(null)} dialogClassName="modal-90w modal-width dashboard-report-modal">
                <Modal.Header closeButton>
                    <Modal.Title>{activeReport?.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{renderModalContent()}</Modal.Body>
            </Modal>
        </div>
    );
};

export default RelatóriosGerais;
